<template>
  <div class="py-12 space-y-6">
    <!-- === HEADER === -->
    <div
      class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 rounded-2xl border border-gray-200 bg-white p-6"
    >
      <div>
        <h1 class="text-3xl font-bold title-color shimmerTarget">
          {{ roleData?.name || '-' }}
        </h1>
      </div>
      <div class="text-gray-700 shimmerTarget text-right">
        Dernière mise à jour :
        <span class="font-semibold">
          {{
            roleData?.updatedAt
              ? Commons.time(roleData.updatedAt, 'DD MMM YYYY HH:mm')
              : '-'
          }}
        </span>
      </div>
    </div>

    <!-- === PERMISSIONS === -->
    <div class="rounded-2xl border border-gray-200 bg-white p-6 shimmerTarget">
      <h3 class="text-lg font-semibold text-gray-800">Permissions</h3>

      <!-- Permissions groupées par catégorie -->
      <div class="mt-4 space-y-4">
        <div
          v-for="(category, categoryKey) in getPermissionsByCategory()"
          :key="categoryKey"
          class="border border-gray-200 rounded-lg p-4"
        >
          <div class="flex items-center gap-2 mb-3">
            <i
              :class="displayableCategories[categoryKey]?.icon"
              :style="{ color: displayableCategories[categoryKey]?.color }"
            ></i>
            <h4 class="font-medium text-gray-800">
              {{ displayableCategories[categoryKey]?.label || categoryKey }}
            </h4>
            <span class="text-xs text-gray-500"
              >({{ category.length }} permissions)</span
            >
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div
              v-for="permKey in category"
              :key="permKey"
              class="flex items-center gap-2 text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded-lg"
            >
              <i :class="allPermissions[permKey]?.icon" class="text-xs"></i>
              <span>{{ allPermissions[permKey]?.label || permKey }}</span>
              <span
                v-if="allPermissions[permKey]?.level"
                class="text-xs px-1.5 py-0.5 rounded ml-auto"
                :style="{
                  backgroundColor:
                    displayableLevels[allPermissions[permKey].level]?.color +
                    '20',
                  color:
                    displayableLevels[allPermissions[permKey].level]?.color,
                }"
              >
                {{ displayableLevels[allPermissions[permKey].level]?.label }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Résumé des niveaux de permissions -->
      <div class="mt-6 pt-4 border-t border-gray-200">
        <h4 class="text-sm font-medium text-gray-700 mb-3">
          Résumé des niveaux d'accès
        </h4>
        <div class="flex flex-wrap gap-2">
          <span
            v-for="(count, level) in getPermissionLevelsCount()"
            :key="level"
            class="text-xs px-2 py-1 rounded"
            :style="{
              backgroundColor: displayableLevels[level]?.color + '20',
              color: displayableLevels[level]?.color,
            }"
          >
            {{ displayableLevels[level]?.label }}: {{ count }}
          </span>
        </div>
      </div>

      <p
        v-if="!roleData?.permissions?.length"
        class="text-gray-500 italic mt-4"
      >
        Aucune permission définie.
      </p>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import Commons from '@/shared/utils/commons'
import displayablePermissions from '@/project/configs/displayablePermissions.json'

const props = defineProps({
  roleData: {
    type: Object,
    default: () => ({}),
  },
})

// Extraire les données displayables
const displayableCategories = computed(() => displayablePermissions.categories)
const displayableLevels = computed(() => displayablePermissions.levels)
const allPermissions = computed(() => displayablePermissions.permissions)

// Grouper les permissions par catégorie
function getPermissionsByCategory() {
  const categorized = {}

  if (!props.roleData?.permissions?.length) {
    return categorized
  }

  props.roleData.permissions.forEach(permKey => {
    const permission = allPermissions.value[permKey]
    if (permission) {
      const category = permission.category
      if (!categorized[category]) {
        categorized[category] = []
      }
      categorized[category].push(permKey)
    } else {
      // Permission non trouvée dans les displayables
      if (!categorized['autres']) {
        categorized['autres'] = []
      }
      categorized['autres'].push(permKey)
    }
  })

  return categorized
}

// Compter les permissions par niveau
function getPermissionLevelsCount() {
  const counts = {
    basic: 0,
    standard: 0,
    admin: 0,
  }

  if (!props.roleData?.permissions?.length) {
    return counts
  }

  props.roleData.permissions.forEach(permKey => {
    const permission = allPermissions.value[permKey]
    if (permission?.level) {
      counts[permission.level]++
    }
  })

  return counts
}
</script>

<style scoped>
.title-color {
  color: #1f2937;
}
</style>
